import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GridsterConfig } from 'angular-gridster2';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { Process, ProcessData } from '../../../../shared/models/process';
import { ProcessType } from '../../../../shared/models/process-type';
import { Template, TEMPLATE_TYPE } from '../../../../shared/models/template';
import { ProcessTypeService } from '../../../../shared/services/process-type.service';
import { ProcessService } from '../../../../shared/services/process.service';
import { WidgetService } from '../../../widgets/widget.service';
import { ReplaySubject, Subscription } from 'rxjs';
import { AuthService } from 'app/shared/services/auth.service';
import { environment } from 'environments/environment';
import { Grid } from 'app/shared/helpers/grid';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';
import { textChangeRangeIsUnchanged } from 'typescript';

@AutoUnsubscribe()
@Component({
  selector: 'process-dialog',
  templateUrl: './process-dialog.component.html',
  styleUrls: ['./process-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ProcessDialogComponent implements OnInit, OnDestroy {
  process: Process;
  processType: ProcessType;
  processData: ProcessData;
  options: GridsterConfig;
  grid = new Grid();
  isNew = true;
  hasChanged = false;
  processTypeName = '';
  canSave = false;
  validationErrors = '';
  readOnly = false;
  userReadOnly = false;
  editTemplate: Template;

  valueChanged: ReplaySubject<any> = new ReplaySubject<any>(1, 100);
  onValueChanged: Subscription;
  onItemChanged: Subscription;

  // dialog size is bound to sizeX and sizeY
  sizeX = 630;
  sizeY = 522;

  // value changed in one of the widgets, set 'hasChanged' flag and update the widget
  handleValueChanged = {
    valueEmitter: (dataItem) => {
      this.hasChanged = true;
      this.valueChanged.next(dataItem);
    },
  };
  saveRecord: boolean = true;

  constructor(
    public dialogRef: MatDialogRef<ProcessDialogComponent>,
    private processService: ProcessService,
    private processTypeService: ProcessTypeService,
    private widgetService: WidgetService,
    private authService: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) { }

  ngOnInit() {
    this.onItemChanged = this.grid.onValueChanged.subscribe((dataItem) => {
      // make sure that the widget metadata persists to the subject
      if (this.process) {
        this.process.setFromMetadata(this.processType, this.grid.values);

        // update any calculated fields...
        this.processType.dataFields
          .filter((i) => i.dataType.toLowerCase() === 'calculated')
          .forEach((datafield) => {
            const item = this.editTemplate.layoutItems.find(
              (i) => i.name.toLowerCase() === datafield.name.toLowerCase()
            );
            if (item) {
              const gridItem = this.grid.items.find((i) => i.id === item.id);
              if (
                gridItem &&
                gridItem.metadata.value !==
                this.process.processData.data[datafield.id]
              ) {
                gridItem.metadata.value =
                  this.process.processData.data[datafield.id];
              }
            }
          });
      }
    });

    this.userReadOnly = this.authService.isUserReadOnly();

    if (this.data.processRec) {
      this.process = new Process(this.data.processRec);
      this.saveRecord = this.data.saveRecord;
    }
    else {
      this.process = new Process(this.data);   
      this.saveRecord = true;
    }

    this.isNew = this.process.id ? false : true;
    this.processTypeService
      .getById(this.process.processTypeId).pipe(take(1))
      .subscribe((processType) => {
        this.processType = new ProcessType(processType);

        // Make form readonly when
        this.readOnly =
          // 1. User is read only
          this.userReadOnly ||
          // 2. The process is a checklist and has an id 
          (this.processType && this.processType.isCheckList===true && this.process && this.process.id>0) ||

          // 3. The process is archived
          (this.process && this.process.archived) ||

          // 4. The process is an outcome and the mode is AAB
          (environment.limitAccessToSubjectsByOrg === true && this.processType.name.toLowerCase() === 'outcome')
          ;
        this.editTemplate = new Template(null, this.processType.editTemplate);
        if (this.editTemplate.layoutItems.length < 1) {
          // we don't have a list template specified, so get a default one
          this.editTemplate = this.processService.getDefaultTemplate(
            TEMPLATE_TYPE.EDIT,
            this.processType
          );
        }

        this.processTypeName = this.processType.name;

        // set dialog size and gridster options based on template size...
        // this has been moved out of dialog into template class to make it more re-usable
        const options = this.editTemplate.getSizeAndOptions(this.grid.size);
        this.sizeX = options.sizeX;
        this.sizeY = options.sizeY;
        this.options = options.options;

        // populate grid
        this.grid.addItems(
          this.process,
          this.widgetService,
          this.editTemplate,
          this.processType,
          undefined,
          undefined,
          this.readOnly
        );
        this.grid.values = this.process.getMetadata(this.processType);

        if (this.data.enableSave) {
          this.valueChanged.next(undefined);
        }
      });

    this.onValueChanged = this.valueChanged
      .pipe(
        debounceTime(200), // 0.2 second debounce
        distinctUntilChanged()
      )
      .subscribe((dataItem) => {
        if (dataItem) {
          this.grid.updateItem(dataItem);
        }
        this.canSave = this.grid.valid;
      });
  }

  ngOnDestroy() {
    this.onValueChanged?.unsubscribe();
    this.onItemChanged?.unsubscribe();
  }

  save() {
    if (this.process) {
      this.canSave = false;

      // update process using metadata
      this.process.setFromMetadata(this.processType, this.grid.values);
      this.processService
        .hydrate(this.process)
        .pipe(take(1))
        .subscribe((_) => {
          // update the summary with any changes - note we must do this after we have hydrated
          // the process so that the correct lookup descriptions have been loaded if/as necessary
          this.process.summary = this.process.getSummary(
            this.processType,
            true
          );

          // set up subscription to close the dialog after save
          this.processService.onAfterSave.pipe(take(1)).subscribe((process) => {
            this.closeDlg(process);
          });

          // if we are not saving the process record here, then just close dlg with the process record in the dlg result data.          
          if (this.saveRecord == false) {
            this.closeDlg();
            return;
          }

          else {
            // save the process
            this.processService.save(this.process, `Process saved`);
          }
        });
    }
  }

  closeDlg(process: Process=undefined, dlgCancelled:boolean=false) {
    this.dialogRef.close({
      process: process || this.process,
      processType: this.processType,
      dlgCancelled: dlgCancelled,
    });
  }

  cancelDlg()
  {
    this.closeDlg(undefined, true);
  }
}
